import { useState } from 'react';
import { BusinessActivity } from '@/types/businessAppTypes';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Grid, List, Plus } from 'lucide-react';
import { ActivityTable } from './ActivityTable';
import { ActivityGrid } from './ActivityGrid';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { motion, AnimatePresence } from 'framer-motion';

interface ActivityTabsProps {
  activities: BusinessActivity[];
  onEditActivity: (activity: BusinessActivity) => void;
  onDeleteActivity: (id: string) => void;
  onCreateActivity: () => void;
}


const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24
    }
  }
};

export function ActivityTabs({
  activities,
  onEditActivity,
  onDeleteActivity,
  onCreateActivity
}: ActivityTabsProps) {
  const [viewType, setViewType] = useState<'table' | 'grid'>('table');

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-4"
    >
      {/* Clean Action Bar */}
      <motion.div variants={itemVariants} className="flex items-center justify-between gap-4">
        <Button 
          onClick={onCreateActivity}
          className="bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 px-4 shadow-md hover:shadow-lg transition-shadow whitespace-nowrap"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Activity
        </Button>

        <TooltipProvider>
          <Tabs value={viewType} onValueChange={(value) => setViewType(value as 'table' | 'grid')} className="w-auto">
            <TabsList className="bg-slate-50/80 p-1 rounded-lg shadow-sm border border-slate-200/60 h-10">
              <TabsTrigger 
                value="table" 
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-3 py-1.5 rounded-md font-medium text-sm h-8"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <List className="h-3 w-3" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Table View</p>
                  </TooltipContent>
                </Tooltip>
              </TabsTrigger>
              <TabsTrigger 
                value="grid" 
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-3 py-1.5 rounded-md font-medium text-sm h-8"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Grid className="h-3 w-3" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Grid View</p>
                  </TooltipContent>
                </Tooltip>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </TooltipProvider>
      </motion.div>

      {/* Enhanced Activity Display with Built-in Search & Filters */}
      <motion.div
        variants={itemVariants}
        className="bg-white rounded-lg shadow-sm border border-slate-200/60"
      >
        <AnimatePresence mode="wait">
          {activities.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="flex flex-col items-center justify-center py-12 px-4 text-center"
            >
              <Search className="h-12 w-12 text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">No activities yet</h3>
              <p className="text-slate-500 mb-4">Start by creating your first activity</p>
              <Button 
                onClick={onCreateActivity}
                className="bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Activity
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="p-4"
            >
              {viewType === 'table' ? (
                <ActivityTable
                  activities={activities}
                  onEdit={onEditActivity}
                  onDelete={onDeleteActivity}
                />
              ) : (
                <ActivityGrid
                  activities={activities}
                  onEdit={onEditActivity}
                  onDelete={onDeleteActivity}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
} 